import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Linkedin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { trackFormSubmission, trackOutboundLink } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to backend API
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      
      // Track successful submission in GA4
      trackFormSubmission('contact_form', true);
      
      toast.success('Thank you for your message! We will be in touch shortly.');
      
      // Clear form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Track failed submission
      trackFormSubmission('contact_form', false);
      
      toast.error('Failed to send message. Please try again or email us directly at Ciaran@downesengineering.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: 'Location',
      content: 'Ireland',
      subtext: 'Serving clients across Ireland and the EU',
    },
    {
      icon: <Mail size={24} />,
      title: 'Email',
      content: 'Ciaran@downesengineering.com',
      link: 'mailto:Ciaran@downesengineering.com',
    },
    {
      icon: <Linkedin size={24} />,
      title: 'LinkedIn',
      content: 'Connect with us',
      link: 'https://www.linkedin.com/company/downes-engineering-consulting',
      subtext: 'Professional network',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#0d1b2e] text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4a9ece] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeIn">
              Get in Touch
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Whether you're exploring a new development opportunity or need advisory support on an existing project, we're here to help. Let's discuss how we can support your objectives.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information Cards */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#0d1b2e] mb-6">Contact Information</h2>
              {contactInfo.map((info, index) => (
                <Card key={index} className="border-slate-200 hover:border-[#4a9ece] transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#4a9ece] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0d1b2e] mb-1">{info.title}</h3>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-slate-600 hover:text-[#4a9ece] transition-colors"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-slate-600">{info.content}</p>
                        )}
                        {info.subtext && (
                          <p className="text-sm text-slate-500 mt-1">{info.subtext}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Additional Info */}
              <Card className="border-slate-200 bg-[#0d1b2e] text-white">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-3">Office Hours</h3>
                  <p className="text-slate-300 text-sm mb-2">Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p className="text-slate-300 text-sm">Weekend: By appointment</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#0d1b2e] mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#0d1b2e] font-medium">
                          Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          className="border-slate-300 focus:border-[#4a9ece] focus:ring-[#4a9ece]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#0d1b2e] font-medium">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your.email@example.com"
                          className="border-slate-300 focus:border-[#4a9ece] focus:ring-[#4a9ece]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#0d1b2e] font-medium">
                        Phone (Optional)
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+353 XX XXX XXXX"
                        className="border-slate-300 focus:border-[#4a9ece] focus:ring-[#4a9ece]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#0d1b2e] font-medium">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project or inquiry..."
                        rows={6}
                        className="border-slate-300 focus:border-[#4a9ece] focus:ring-[#4a9ece] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#4a9ece] hover:bg-[#3a8ebe] text-white py-6 text-lg"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2" size={18} />
                    </Button>
                  </form>

                  <p className="text-sm text-slate-500 mt-6 text-center">
                    We typically respond within 24 hours during business days.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder / Additional CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
              Serving Clients Across Ireland and the EU
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
              Based in Ireland, we provide engineering-led advisory services to property developers, investors, and asset owners throughout the country and across the European Union. Our expertise spans residential, commercial, and mixed-use developments in diverse regulatory and market contexts.
            </p>
            <div className="bg-slate-100 rounded-lg p-12 border-2 border-slate-200">
              <MapPin size={48} className="text-[#4a9ece] mx-auto mb-4" />
              <p className="text-slate-600 font-medium">
                Ireland • European Union
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
